import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaGraduationCap, 
  FaGlobe, 
  FaTrophy, 
  FaRocket, 
  FaCheckCircle,
  FaArrowRight,
  FaStar,
  FaHandshake,
  FaLightbulb,
  FaUsers,
  FaAward,
  FaChartLine,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt
} from "react-icons/fa";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import { getFormApiUrl } from "../utils/formApiUrl";

const ICON_MAP = {
  graduation: FaGraduationCap, globe: FaGlobe, trophy: FaTrophy, rocket: FaRocket,
  lightbulb: FaLightbulb, users: FaUsers, award: FaAward, chart: FaChartLine,
  phone: FaPhone, email: FaEnvelope, location: FaMapMarkerAlt, star: FaStar,
};

const FALLBACK_HERO = { title: 'Your Future\nStarts Here', subtitle: 'Transform your dreams into reality at JRIIT. Join a community of innovators, creators, and future leaders. Your journey to excellence begins with one bold step.', primaryButtonText: 'Start Your Application' };
const FALLBACK_STATS = { title: 'JRIIT at a Glance', subtitle: 'Join thousands of successful graduates who transformed their lives through quality education', items: [{ number: 391, label: 'Undergraduate Students', icon: 'graduation' }, { number: 25, label: 'Tanzania Regions', icon: 'globe' }, { number: 1411, label: 'Graduate Students', icon: 'trophy' }] };
const FALLBACK_FEATURES = { title: 'Why Choose JRIIT?', subtitle: 'Discover what makes JRIIT the premier choice for your educational journey', items: [{ title: 'Innovation First', description: 'Cutting-edge curriculum designed for the digital age', icon: 'lightbulb' }, { title: 'Expert Faculty', description: 'Learn from industry professionals and academic leaders', icon: 'users' }, { title: 'Industry Recognition', description: 'Accredited programs with strong industry partnerships', icon: 'award' }, { title: 'Career Growth', description: '95% employment rate with competitive starting salaries', icon: 'chart' }] };
const FALLBACK_PROGRAMS = [
  { name: 'Cyber Security', slug: 'cyber-security', description: 'Master the latest technologies and industry practices in cyber security' },
  { name: 'Travel and Tourism', slug: 'travel-tourism-management', description: 'Master the latest technologies and industry practices in travel and tourism' },
  { name: 'Information Technology', slug: 'information-technology', description: 'Master the latest technologies and industry practices in information technology' },
  { name: 'Business Administration', slug: 'business-administration', description: 'Master the latest technologies and industry practices in business administration' },
  { name: 'Professional Courses', slug: 'professional-courses', description: 'Gain industry-ready skills with our specialized professional courses' },
  { name: 'Short Courses', slug: 'short-courses', description: 'Boost your skills quickly with our intensive short courses' },
];
const FALLBACK_TESTIMONIALS = [
  { name: 'Sarah Mwangi', role: 'Computer Science Graduate', image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face', quote: 'JRIIT transformed my career. The practical approach and industry connections opened doors I never imagined possible.', rating: 5 },
  { name: 'David Kimani', role: 'Business Administration Student', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face', quote: 'The supportive environment and real-world projects prepared me for success. I\'m confident about my future.', rating: 5 },
  { name: 'Grace Ochieng', role: 'Cyber Security Graduate', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face', quote: 'JRIIT\'s cutting-edge curriculum and expert faculty gave me the skills to excel in the digital security field.', rating: 5 },
];
const FALLBACK_CONTACT = [
  { icon: 'phone', title: 'Call Us', info: '+255 754 360 590', description: 'Speak directly with our admissions team' },
  { icon: 'email', title: 'Email Us', info: 'admissions@jriit.ac.tz', description: 'Send us your questions and inquiries' },
  { icon: 'location', title: 'Visit Us', info: 'Sakina Area, Arusha', description: 'Schedule a campus tour and meet our team' },
];
const FALLBACK_FORM = { title: 'Ready to Begin Your Journey?', subtitle: 'Take the first step towards your future. Our streamlined application process makes it easy to get started.' };
const FALLBACK_CTA = { title: 'Ready to Transform Your Future?', subtitle: 'Join thousands of students who have already taken the first step towards their dreams. Your success story starts here.', buttonText: 'Start Your Application Today' };

function safeParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

export default function Admission() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", phone: "", email: "",
    schoolName: "", completionYear: "", program: "",
    shortCourse: "",
    additionalInfo: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [counters, setCounters] = useState({ students: 0, regions: 0, graduates: 0 });
  
  const [hero, setHero] = useState(FALLBACK_HERO);
  const [statsSection, setStatsSection] = useState(FALLBACK_STATS);
  const [featuresSection, setFeaturesSection] = useState(FALLBACK_FEATURES);
  const [programs, setPrograms] = useState(FALLBACK_PROGRAMS);
  const [programsMeta, setProgramsMeta] = useState({ title: 'Transform Your Future', subtitle: 'Choose from our comprehensive range of programs designed to prepare you for the digital age' });
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [testimonialsMeta, setTestimonialsMeta] = useState({ title: 'Success Stories', subtitle: 'Hear from our graduates about their transformative journey at JRIIT' });
  const [contactSection, setContactSection] = useState({ title: 'Get in Touch', subtitle: 'Have questions? Our admissions team is here to help you every step of the way', items: FALLBACK_CONTACT });
  const [formSection, setFormSection] = useState(FALLBACK_FORM);
  const [ctaSection, setCtaSection] = useState(FALLBACK_CTA);
  /** When null, no CMS payload — keep full static fallbacks. When set, only these sections render (API returns active only). */
  const [visibleSectionKeys, setVisibleSectionKeys] = useState(null);

  const containerRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await cmsApi.getPageComponents('admissions');
        if (!result?.success) return;
        const list = result.components || [];
        if (list.length === 0) return;

        const map = {};
        const keys = [];
        for (const c of list) {
          map[c.componentName] = c;
          keys.push(c.componentName);
        }
        setVisibleSectionKeys(keys);

        if (map['hero']) {
          const hc = safeParse(map['hero'].content, {});
          setHero({
            title: map['hero'].title || FALLBACK_HERO.title,
            subtitle: map['hero'].subtitle || FALLBACK_HERO.subtitle,
            primaryButtonText: hc.primaryButtonText || FALLBACK_HERO.primaryButtonText,
          });
        }
        if (map['stats']) {
          setStatsSection({
            title: map['stats'].title || FALLBACK_STATS.title,
            subtitle: map['stats'].subtitle || FALLBACK_STATS.subtitle,
            items: safeParse(map['stats'].content, FALLBACK_STATS.items),
          });
        }
        if (map['features']) {
          setFeaturesSection({
            title: map['features'].title || FALLBACK_FEATURES.title,
            subtitle: map['features'].subtitle || FALLBACK_FEATURES.subtitle,
            items: safeParse(map['features'].content, FALLBACK_FEATURES.items),
          });
        }
        if (map['programs']) {
          setProgramsMeta({ title: map['programs'].title || 'Transform Your Future', subtitle: map['programs'].subtitle || '' });
          setPrograms(safeParse(map['programs'].content, FALLBACK_PROGRAMS));
        }
        if (map['testimonials']) {
          setTestimonialsMeta({ title: map['testimonials'].title || 'Success Stories', subtitle: map['testimonials'].subtitle || '' });
          setTestimonials(safeParse(map['testimonials'].content, FALLBACK_TESTIMONIALS));
        }
        if (map['contact']) {
          setContactSection({
            title: map['contact'].title || 'Get in Touch',
            subtitle: map['contact'].subtitle || '',
            items: safeParse(map['contact'].content, FALLBACK_CONTACT),
          });
        }
        if (map['application-form']) {
          setFormSection({
            title: map['application-form'].title || FALLBACK_FORM.title,
            subtitle: map['application-form'].subtitle || FALLBACK_FORM.subtitle,
          });
        }
        if (map['cta']) {
          const ct = safeParse(map['cta'].content, {});
          setCtaSection({
            title: map['cta'].title || FALLBACK_CTA.title,
            subtitle: map['cta'].subtitle || FALLBACK_CTA.subtitle,
            buttonText: ct.buttonText || FALLBACK_CTA.buttonText,
          });
        }
      } catch (err) {
        console.log('Using default admissions content:', err.message);
      }
    };
    loadData();
  }, []);

  const showCmsSection = (key) =>
    visibleSectionKeys == null || visibleSectionKeys.includes(key);

  const scrollToSectionWithOffset = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const navbar = document.querySelector("header.sticky");
    const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 140;
    const extraOffset = 16;
    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: elementTop - navbarHeight - extraOffset, behavior: "smooth" });
  };

  // programs is now a state variable loaded from CMS

  const shortCourses = [
    "Basic Computer Application",
    "Graphics Design",
    "Web Design and Development",
    "Computer Maintenance and Repair (A+)",
    "Networking N+",
    "Computer Programming",
    "Others",
  ];

  // testimonials and stats are now state variables loaded from CMS

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const increment = 391 / steps;
      const incrementRegions = 25 / steps;
      const incrementGraduates = 1411 / steps;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        setCounters({
          students: Math.min(Math.round(increment * step), 391),
          regions: Math.min(Math.round(incrementRegions * step), 25),
          graduates: Math.min(Math.round(incrementGraduates * step), 1411)
        });
        if (step >= steps) clearInterval(timer);
      }, duration / steps);
      return () => clearInterval(timer);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    const statsSection = document.getElementById('stats-section');
    if (statsSection) observer.observe(statsSection);
    return () => observer.disconnect();
  }, []);

  const getValidationErrors = (step = 4) => {
    const errors = {};
    if (step >= 1) {
      if (!formData.firstName.trim()) errors.firstName = "First name is required";
      if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    }
    if (step >= 2) {
      if (!formData.phone.trim()) errors.phone = "Phone number is required";
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      }
    }
    if (step >= 3) {
      if (!formData.schoolName.trim()) errors.schoolName = "School name is required";
      if (!formData.completionYear.trim()) errors.completionYear = "Completion year is required";
    }
    if (step >= 4) {
      if (!formData.program) errors.program = "Please select a program";
      if (formData.program === "Short Courses" && !formData.shortCourse.trim()) {
        errors.shortCourse = "Please select a short course";
      }
      if (!formData.additionalInfo.trim()) {
        errors.additionalInfo = "Additional information is required";
      }
    }
    return errors;
  };

  const validateStep = (step) => {
    const errors = getValidationErrors(step);
    setFormErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    const errors = getValidationErrors(4);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "program") {
        if (value !== "Short Courses") {
          next.shortCourse = "";
        }
      }
      return next;
    });
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const emailBody = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background: #f4f4f4; }
            .container { max-width: 650px; margin: 0 auto; background: white; }
            .header { background: linear-gradient(135deg, #9333ea 0%, #3b82f6 100%); color: white; padding: 40px 30px; text-align: center; }
            .header h1 { margin: 0; font-size: 28px; }
            .header p { margin: 10px 0 0 0; opacity: 0.9; }
            .content { padding: 30px; }
            .section { margin-bottom: 25px; padding: 20px; background: #f9fafb; }
            .section-title { font-weight: bold; color: #9333ea; margin-bottom: 15px; font-size: 18px; text-transform: uppercase; letter-spacing: 0.5px; }
            .field { margin-bottom: 12px; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
            .field:last-child { border-bottom: none; }
            .label { font-weight: 600; color: #4b5563; display: inline-block; min-width: 140px; }
            .value { color: #1f2937; word-wrap: break-word; }
            .footer { background: #1f2937; color: white; padding: 25px; text-align: center; font-size: 13px; }
            .footer p { margin: 5px 0; }
            .badge { display: inline-block; background: #9333ea; color: white; padding: 6px 12px; border-radius: 0; font-size: 11px; font-weight: bold; margin-left: 14px; vertical-align: middle; letter-spacing: 0.03em; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header"><h1>New Admission Application</h1><p>JRIIT College - Admissions Office</p></div>
            <div class="content">
              <div class="section">
                <div class="section-title">Personal Information</div>
                <div class="field"><span class="label">Full Name:</span><span class="value">${formData.firstName} ${formData.lastName}</span></div>
                <div class="field"><span class="label">Phone Number:</span><span class="value">${formData.phone}</span></div>
                <div class="field"><span class="label">Email Address:</span><span class="value">${formData.email || 'Not provided'}</span></div>
              </div>
              <div class="section">
                <div class="section-title">Education Background</div>
                <div class="field"><span class="label">School Name:</span><span class="value">${formData.schoolName}</span></div>
                <div class="field"><span class="label">Year Completed:</span><span class="value">${formData.completionYear}</span></div>
              </div>
              <div class="section">
                <div class="section-title">Program Selection</div>
                <div class="field"><span class="label">Selected Program:</span><span class="value">${formData.program}<span class="badge">PRIMARY PROGRAM</span></span></div>
                ${
                  formData.program === "Short Courses" && formData.shortCourse
                    ? `<div class="field"><span class="label">Short Course:</span><span class="value">${formData.shortCourse}</span></div>`
                    : ""
                }
              </div>
              <div class="section">
                <div class="section-title">Additional Information</div>
                <div class="value" style="line-height: 1.8;">${formData.additionalInfo.replace(/\n/g, '<br>')}</div>
              </div>
            </div>
            <div class="footer">
              <p><strong>JRIIT College Admissions Office</strong></p>
              <p>Sakina Area, Arusha Tanzania | P.O. Box 7071 Namanga Road</p>
              <p>+255 754 360 590 | admissions@jriit.ac.tz</p>
              <p style="margin-top: 15px; opacity: 0.8;">Application received on: ${new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'long', timeZone: 'Africa/Dar_es_Salaam' })}</p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Local: same-origin /api/send-admission → setupProxy → Express. Production: CMS public API.
      const admissionUrl = getFormApiUrl('send-admission');
      const response = await fetch(admissionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: `${formData.firstName} ${formData.lastName} - ${formData.program}`,
          body: emailBody,
          replyTo: formData.email,
          replyToName: `${formData.firstName} ${formData.lastName}`,
        }),
      });

      const rawText = await response.text();
      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: ${rawText.slice(0, 300)}`
        );
      }
      let result;
      try {
        result = JSON.parse(rawText || '{}');
      } catch {
        throw new Error(
          'Server returned non-JSON (often HTML from the dev server). Run the email API on port 5000 (node backend/server.js) and use npm start for the portal.'
        );
      }

      if (result.success) {
        setSubmitSuccess(true);
      } else {
        alert("There was an error submitting your application. Please try again or contact us directly.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("There was an error submitting your application. Please try again or contact us at principal@jriit.ac.tz");
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4 && validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const dismissSuccessModal = () => {
    setSubmitSuccess(false);
    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      schoolName: "",
      completionYear: "",
      program: "",
      shortCourse: "",
      additionalInfo: "",
    });
    setCurrentStep(1);
  };

  const inputCls = (field) => `w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-custom-pink/20 transition-all duration-300 ${formErrors[field] ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-custom-pink'}`;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section */}
      {showCmsSection("hero") && (
      <section className="relative bg-gradient-to-br from-custom-pink via-purple-900 to-indigo-900 text-white py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/5 rounded-full blur-xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/5 rounded-full blur-xl" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {hero.title.split('\n').map((line, i) => i === 0 ? <span key={i}>{line}</span> : <span key={i} className="block text-gold">{line}</span>)}
          </motion.h1>
          
          <motion.p 
            className="text-lg sm:text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed opacity-90"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <button
              className="bg-gold text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-yellow-500 transition-all duration-300 flex items-center gap-2 shadow-lg"
              onClick={() => scrollToSectionWithOffset('application-form')}
            >
              {hero.primaryButtonText}
              <FaArrowRight />
            </button>
          </motion.div>
        </div>
      </section>
      )}

      {/* Statistics Section */}
      {showCmsSection("stats") && (
      <section id="stats-section" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{statsSection.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{statsSection.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {statsSection.items.map((stat, index) => {
              const IconComp = ICON_MAP[stat.icon] || FaGraduationCap;
              const colors = ['text-custom-pink bg-custom-pink/10', 'text-green-600 bg-green-50', 'text-gold bg-gold/10'];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 text-center shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${colors[index % colors.length]} flex items-center justify-center text-2xl`}>
                    <IconComp />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">
                    {index === 0 ? counters.students : index === 1 ? counters.regions : counters.graduates}
                  </div>
                  <p className="text-gray-600">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Why Choose JRIIT Section */}
      {showCmsSection("features") && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{featuresSection.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{featuresSection.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {featuresSection.items.map((feature, index) => {
              const IconComp = ICON_MAP[feature.icon] || FaLightbulb;
              const colors = ['text-blue-600 bg-blue-50', 'text-custom-pink bg-custom-pink/10', 'text-green-600 bg-green-50', 'text-gold bg-gold/10'];
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className="group">
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg hover:border-custom-pink/30 transition-all duration-300 h-full">
                    <div className={`w-14 h-14 ${colors[index % colors.length]} rounded-2xl flex items-center justify-center text-2xl mb-5 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComp />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Programs Section */}
      {showCmsSection("programs") && (
      <section id="programs-section" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{programsMeta.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{programsMeta.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg hover:border-custom-pink/30 transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-custom-pink/10 text-custom-pink rounded-2xl flex items-center justify-center text-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                    <FaRocket />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-custom-pink transition-colors duration-300">{program.name}</h3>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed">{program.description}</p>
                  <a href={`/course/${program.slug}`} className="inline-flex items-center text-custom-pink font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                    Learn More <FaArrowRight className="ml-2" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Testimonials Section */}
      {showCmsSection("testimonials") && (
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{testimonialsMeta.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{testimonialsMeta.subtitle}</p>
          </div>

          <div className="relative" style={{ minHeight: 380 }}>
            <AnimatePresence initial={false}>
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-x-0 top-0 bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-200 shadow-lg"
              >
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full overflow-hidden border-4 border-gold shadow-md bg-gray-200 flex items-center justify-center">
                    {testimonials[activeTestimonial].image ? (
                      <img
                        src={testimonials[activeTestimonial].image}
                        alt={testimonials[activeTestimonial].name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-full h-full items-center justify-center bg-custom-pink/10 text-custom-pink text-2xl font-bold ${testimonials[activeTestimonial].image ? 'hidden' : 'flex'}`}>
                      {testimonials[activeTestimonial].name ? testimonials[activeTestimonial].name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'}
                    </div>
                  </div>
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <FaStar key={i} className="text-gold text-xl mx-1" />
                    ))}
                  </div>
                  <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic leading-relaxed max-w-2xl mx-auto">
                    "{testimonials[activeTestimonial].quote}"
                  </blockquote>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">{testimonials[activeTestimonial].name}</h4>
                  <p className="text-custom-pink font-semibold text-sm">{testimonials[activeTestimonial].role}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex justify-center mt-6 gap-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeTestimonial ? 'bg-custom-pink scale-125' : 'bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Contact Information Section */}
      {showCmsSection("contact") && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{contactSection.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{contactSection.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {contactSection.items.map((contact, index) => {
              const IconComp = ICON_MAP[contact.icon] || FaPhone;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.15 }} viewport={{ once: true }}>
                  <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300 text-center h-full">
                    <div className="w-14 h-14 bg-custom-pink/10 text-custom-pink rounded-2xl flex items-center justify-center text-2xl mx-auto mb-5">
                      <IconComp />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{contact.title}</h3>
                    <p className="text-custom-pink font-semibold mb-2">{contact.info}</p>
                    <p className="text-gray-500 text-sm">{contact.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Application Form Section */}
      {showCmsSection("application-form") && (
      <section id="application-form" ref={containerRef} className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">{formSection.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{formSection.subtitle}</p>
          </div>

          <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 border border-gray-200 shadow-xl">
            {/* Progress Steps */}
            <div className="flex justify-between mb-10">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex flex-col items-center">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base font-bold transition-all duration-300 ${step <= currentStep ? 'bg-custom-pink text-white shadow-md' : 'bg-gray-100 text-gray-400 border border-gray-200'}`}>
                    {step < currentStep ? <FaCheckCircle /> : step}
                  </div>
                  <span className={`text-xs sm:text-sm mt-2 transition-colors duration-300 text-center ${step <= currentStep ? 'text-custom-pink font-medium' : 'text-gray-400'}`}>
                    {step === 1 ? 'Personal' : step === 2 ? 'Contact' : step === 3 ? 'Education' : 'Program'}
                  </span>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {currentStep === 1 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-gray-700 font-medium">First Name *</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className={inputCls('firstName')} placeholder="Enter your first name" required />
                      {formErrors.firstName && <p className="text-red-500 text-sm">{formErrors.firstName}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-gray-700 font-medium">Last Name *</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={inputCls('lastName')} placeholder="Enter your last name" required />
                      {formErrors.lastName && <p className="text-red-500 text-sm">{formErrors.lastName}</p>}
                    </div>
                  </div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-gray-700 font-medium">Phone Number *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputCls('phone')} placeholder="Enter your phone number" required />
                      {formErrors.phone && <p className="text-red-500 text-sm">{formErrors.phone}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-gray-700 font-medium">Email Address</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputCls('email')} placeholder="Enter your email address" />
                      {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
                    </div>
                  </div>
                </motion.div>
              )}

              {currentStep === 3 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Education Background</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-gray-700 font-medium">O' Level Secondary School *</label>
                      <input type="text" name="schoolName" value={formData.schoolName} onChange={handleChange} className={inputCls('schoolName')} placeholder="Enter school name" required />
                      {formErrors.schoolName && <p className="text-red-500 text-sm">{formErrors.schoolName}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-gray-700 font-medium">Year Completed *</label>
                      <input type="text" name="completionYear" value={formData.completionYear} onChange={handleChange} className={inputCls('completionYear')} placeholder="Enter completion year" required />
                      {formErrors.completionYear && <p className="text-red-500 text-sm">{formErrors.completionYear}</p>}
                    </div>
                  </div>
                </motion.div>
              )}

              {currentStep === 4 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Program Selection</h3>
                  <div className="space-y-4">
                    <label className="text-gray-700 font-medium">Select Programme *</label>
                    <div className="grid grid-cols-1 gap-3">
                      {programs.map((prog) => (
                        <label key={prog.name} className={`flex items-center p-4 bg-gray-50 border rounded-xl cursor-pointer hover:border-custom-pink/50 transition-all duration-300 ${formData.program === prog.name ? 'border-custom-pink bg-custom-pink/5' : 'border-gray-200'}`}>
                          <input type="radio" name="program" value={prog.name} checked={formData.program === prog.name} onChange={handleChange} className="w-5 h-5 text-custom-pink focus:ring-custom-pink border-gray-300" />
                          <span className="ml-3 text-gray-800">{prog.name}</span>
                        </label>
                      ))}
                    </div>
                    {formErrors.program && <p className="text-red-500 text-sm">{formErrors.program}</p>}
                  </div>

                  {formData.program === "Short Courses" && (
                    <div className="space-y-4">
                      <label className="text-gray-700 font-medium">Select Short Course *</label>
                      <select
                        name="shortCourse"
                        value={formData.shortCourse}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-800 focus:border-custom-pink focus:ring-2 focus:ring-custom-pink/20 transition-all duration-300 ${
                          formErrors.shortCourse ? "border-red-400" : "border-gray-200"
                        }`}
                        required={formData.program === "Short Courses"}
                      >
                        <option value="" disabled>
                          Choose a short course…
                        </option>
                        {shortCourses.map((course) => (
                          <option key={course} value={course}>
                            {course}
                          </option>
                        ))}
                      </select>
                      {formErrors.shortCourse && (
                        <p className="text-red-500 text-sm">{formErrors.shortCourse}</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-4">
                    <label className="text-gray-700 font-medium">Additional Information *</label>
                    <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows={4} className={`${inputCls('additionalInfo')} resize-none`} placeholder="Tell us more about your goals and interests..." required />
                    {formErrors.additionalInfo && <p className="text-red-500 text-sm">{formErrors.additionalInfo}</p>}
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6">
                {currentStep > 1 && (
                  <button type="button" onClick={prevStep} className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium">
                    Previous
                  </button>
                )}
                
                {currentStep < 4 ? (
                  <button type="button" onClick={nextStep} className="ml-auto px-8 py-3 bg-custom-pink text-white rounded-xl hover:bg-purple-800 transition-all duration-300 font-medium shadow-md">
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`ml-auto px-8 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-custom-pink hover:bg-purple-800 shadow-md'} text-white`}
                  >
                    {isSubmitting ? (
                      <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting...</>
                    ) : submitSuccess ? (
                      <><FaCheckCircle className="text-green-400" /> Submitted Successfully!</>
                    ) : (
                      <>Submit Application <FaRocket className="ml-1" /></>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      {showCmsSection("cta") && (
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{ctaSection.title}</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">{ctaSection.subtitle}</p>
          <button
            className="bg-gold text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-yellow-500 transition-all duration-300 shadow-lg flex items-center gap-3 mx-auto"
            onClick={() => scrollToSectionWithOffset('application-form')}
          >
            <FaHandshake className="text-2xl" />
            {ctaSection.buttonText}
          </button>
        </div>
      </section>
      )}

      {/* Success Modal */}
      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admission-success-title"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={dismissSuccessModal}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-6xl text-green-500" />
              </div>
              <h3 id="admission-success-title" className="text-2xl font-bold text-gray-900 mb-4">
                Application Submitted!
              </h3>
              <p className="text-gray-600 mb-6">Thank you for your interest in JRIIT! We've received your application and will be in touch soon.</p>
              <button
                type="button"
                className="bg-custom-pink text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-800 transition-colors"
                onClick={dismissSuccessModal}
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
}
